import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Activity, AlertOctagon, Clock, Database, Globe, Monitor, Server } from "lucide-react";
import { useMonitoring } from "@/hooks/useMonitoring";
import { PerformanceCharts } from "@/components/monitoring/PerformanceCharts";
import { AlertsPanel } from "@/components/monitoring/AlertsPanel";
import { ErrorLogsTable } from "@/components/monitoring/ErrorLogsTable";
import { FrontendMetrics } from "@/components/monitoring/FrontendMetrics";
import { VpsMetrics } from "@/components/monitoring/VpsMetrics";
import { SupabaseMetrics } from "@/components/monitoring/SupabaseMetrics";
import { ServerStatus } from "@/components/monitoring/ServerStatus";
import { useFrontendMetrics } from "@/hooks/useFrontendMetrics";
import { useToast } from "@/hooks/use-toast";

  const MetricsCards = () => (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Erros Detectados</CardTitle>
          <AlertOctagon className="h-4 w-4 text-red-500" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">0</div>
          <p className="text-xs text-muted-foreground">Últimas 24 horas</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Performance</CardTitle>
          <Activity className="h-4 w-4 text-blue-500" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">100ms</div>
          <p className="text-xs text-muted-foreground">Tempo de carregamento</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Uso de Memória</CardTitle>
          <Database className="h-4 w-4 text-yellow-500" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">128MB</div>
          <p className="text-xs text-muted-foreground">Consumo atual</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Status</CardTitle>
          <Clock className="h-4 w-4 text-green-500" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">Online</div>
          <p className="text-xs text-muted-foreground">Sistema operacional</p>
        </CardContent>
      </Card>
    </div>
  );

const Monitoring = () => {
  useMonitoring("monitoring");
  useFrontendMetrics();
  const { toast } = useToast();

  const handleRefresh = () => {
    toast({
      title: "Atualizando dados",
      description: "Os dados estão sendo atualizados...",
    });
  };

  return (
    <div className="container mx-auto p-4 space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-3xl font-bold tracking-tight">Monitoramento</h2>
        <Button onClick={handleRefresh} variant="outline">
          Atualizar Dados
        </Button>
      </div>

      <ServerStatus />

      <Tabs defaultValue="general" className="w-full">
        <TabsList className="w-full justify-start">
          <TabsTrigger value="general" className="flex items-center gap-2">
            <Activity className="h-4 w-4" />
            Métricas Gerais
          </TabsTrigger>
          <TabsTrigger value="frontend" className="flex items-center gap-2">
            <Globe className="h-4 w-4" />
            Frontend
          </TabsTrigger>
          <TabsTrigger value="vps" className="flex items-center gap-2">
            <Server className="h-4 w-4" />
            Servidor VPS
          </TabsTrigger>
          <TabsTrigger value="supabase" className="flex items-center gap-2">
            <Database className="h-4 w-4" />
            Supabase
          </TabsTrigger>
        </TabsList>

        <div className="mt-6">
          <TabsContent value="general" className="space-y-6">
            <MetricsCards />
            <div className="grid gap-6 md:grid-cols-2">
              <PerformanceCharts />
              <AlertsPanel />
            </div>
            <Card>
              <CardHeader>
                <CardTitle>Logs de Erro</CardTitle>
              </CardHeader>
              <CardContent>
                <ErrorLogsTable />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="frontend">
            <FrontendMetrics />
          </TabsContent>

          <TabsContent value="vps">
            <VpsMetrics />
          </TabsContent>

          <TabsContent value="supabase">
            <SupabaseMetrics />
          </TabsContent>
        </div>
      </Tabs>
    </div>
  );
};

export default Monitoring;
